/*
 * Copyright © 2026. Legalbird eine Marke der Legalbird GmbH
 */

import { Person } from "./Person";
import { CategoryChange } from "./CategoryChange";
import type { PartnerIri } from "../../constants/partnerIris";
import { CaseInsurance } from "./CaseInsurance";

export const contractLawProductClassNames = [
  "eventContract",
  "membershipContract",
  "otherContract",
  "providerContract",
  "purchaseContract",
  "serviceContract",
  "subscriptionContract",
  "travelContract",
  "vehicleContract",
] as const;

const litigationProductClassNames = [
  ...contractLawProductClassNames,
  "dataLeakContract",
  "employmentContract",
  "rentalContract",
  "trafficAccident",
  "generalCivilLaw",
  "civilServiceLaw",
] as const;

export enum CaseCategorySelectionMode {
  CUSTOMER_SELECTABLE = "customerSelectable",
  FIXED = "fixed",
}

export const productClassNames = [
  "familyLawCase",
  "divorce",
  "alimony",
  "death",
  "shortTimeWork",
  "settlement",
  "traffic",
  "processCostSupport",
  "pendingCase",
  ...litigationProductClassNames,
] as const;

export type LinkedAdministrativeLawCaseIds = {
  mainCaseId: number | null;
  expeditedProceedingCaseId: number | null;
  failureToActLawsuitCaseId: number | null;
};

export interface AbstractCase {
  "@id": string;
  id: number;
  leadStatus: number;
  applicationStatus: number;
  name: (typeof productClassNames)[number];
  reference: string;
  isCompleted: boolean;
  productCategory: "familyLaw" | "trafficLaw" | "employmentLaw" | "contractLaw" | "tenancyLaw" | "civilLaw" | "administrativeLaw";
  created?: string | null;
  caseCategory?: string | null;
  caseCategorySelectionMode: CaseCategorySelectionMode;
  backofficeCase?: string | null;
  assessmentRejectionText?: string | null;
  caseCategoryAssessment?: string | null;
  additionalClients?: Person[];
  signatories?: Person[];
  acquisitionPartner?: PartnerIri | null;
  linkedAdministrativeLawCaseIds?: LinkedAdministrativeLawCaseIds | null;
  administrativeCaseType: "main" | "expeditedProceeding" | "failureToActLawsuit";
  categoryChange: CategoryChange | null;
  isCustomerDataCompleted: boolean;
  paymentType: string | null;
  caseInsurance: CaseInsurance,
  [additionalProperty: string]: any;
}

export function isLitigationCase(product: AbstractCase): product is AbstractCase & { name: (typeof litigationProductClassNames)[number] } {
  return (litigationProductClassNames as readonly string[]).includes(product.name as string);
}

export function isProductClassName(value: unknown): value is (typeof productClassNames)[number] {
  return typeof value === "string" && (productClassNames as readonly string[]).includes(value);
}
